import type { ReactNode } from "react";
import { useIsFormItemError } from "../FormLayout/FormItemErrorProvider";
import { useControlPresentation } from "./Internal/useControlPresentation";
import { shellBox, shellUnderline } from "./Internal/textInputStyles";

interface FieldShellProps {
  /**
   * The field content. Typically a third-party control that renders its own
   * element (e.g. a Stripe card iframe), or any custom content that needs to
   * read as a boxed input. Fills the available width; pair with `endAdornment`
   * for a pinned trailing element.
   */
  children: ReactNode;
  /**
   * Optional trailing element pinned to the right edge (e.g. a card-brand
   * logo), matching how a card-number field shows the detected brand. The
   * `children` take the remaining width to its left.
   */
  endAdornment?: ReactNode;
  /**
   * Force the focused (brand) border. Native content lights the border via
   * `:focus-within`, but a cross-origin iframe (Stripe) won't reliably trigger
   * it — drive this from the embedded control's own focus/blur events instead.
   */
  focused?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
}

/**
 * An input shell for content that isn't a native `<input>` — the chrome of
 * `TextInput` without the input itself, so an embedded control sits flush
 * beside real ui-core inputs. Follows the ambient `ControlPresentation`: the
 * boxed border in a dense form, the underline in the spacious customer flows.
 * Reads `useIsFormItemError()` so a `FormItem error=` turns the border red.
 *
 * Note that the embedded control still styles its own text — an iframe can't
 * inherit ours — so a caller must match the presentation's value text itself
 * (see `creditCardsStripeFieldOptions` in the dashboard).
 */
export function FieldShell({
  children,
  endAdornment,
  focused,
  disabled,
  "data-testid": dataTestid,
}: FieldShellProps) {
  const isError = useIsFormItemError();
  const presentation = useControlPresentation();
  const chrome =
    presentation === "underlined"
      ? shellUnderline(isError, focused === true)
      : shellBox(isError, focused === true);
  return (
    <div
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      className={`flex w-full items-center gap-2 ${chrome} ${
        disabled ? "pointer-events-none opacity-40" : ""
      }`}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {endAdornment !== undefined && (
        <div className="flex shrink-0 items-center">{endAdornment}</div>
      )}
    </div>
  );
}
